import React, { useEffect, useState } from "react";
import styled from "styled-components";

import Content from "../components/content";
import Loader from "../components/loader";
import BasicTitle from "../components/title";
import Button from "@mui/material/Button";

import { DataGrid } from "@mui/x-data-grid";

import { useImpersonation } from "../global/impersonation";
import Api from "../libs/api";
import Storage from "../libs/storage";
import { Box, Tooltip } from "@mui/material";

const Holder = styled.div`
    display: flex;
    height: 100%;
    width: 100%;
    overflow: auto;
`;

const PlanSection = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px;
    background: #f5f5f5;
    border-radius: 8px;
    margin-bottom: 40px;
    gap: 32px;
`;

const PlanHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;

    @media (max-width: 455px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const PlanInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;

    @media (max-width: 455px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const PlanSubtitle = styled.span`
    font-family: "Montserrat";
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    color: #fef2f2;
    border-radius: 8px;
    border: 1px solid #ffffff00;
    background: ${(props) => props.active ? "#007910" : "#CD0000"};
    padding: 2px 8px;
`;

const PlanDetails = styled.div`
    display: flex;
    gap: 80px;
    flex-wrap: wrap;

    @media (max-width: 600px) {
        gap: 32px;
    }
`;

const PlanDetailItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PlanDetailLabel = styled.div`
    font-family: "Montserrat";
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #666666;
`;

const PlanDetailValue = styled.div`
    font-family: "Montserrat";
    font-weight: 600;
    font-size: 20px;
    line-height: 32px;
    color: #181122;
`;

const UsersProgress = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 200px;
`;

const ProgressBar = styled.div`
    width: 100%;
    height: 8px;
    background: #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
`;

const ProgressFill = styled.div`
    height: 100%;
    background: #57c4c2;
    border-radius: 4px;
    transition: width 0.3s ease;
`;

const ProgressText = styled.div`
    font-family: "Montserrat";
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #666666;
`;

const Tag = styled.div`
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 12px;
    text-align: center;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 8px;

    &.pendente {
        color: #000;
        background: transparent;
        border: 1px solid #000;
    }
    &.vencida {
        color: #de6061;
        background: transparent;
        border: 1px solid #de6061;
    }
    &.pago {
        color: #57c4c2;
        background: transparent;
        border: 1px solid #57c4c2;
    }
`;

const ActionLink = styled.a`
    font-family: "Montserrat";
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    color: #57c4c2;
    cursor: pointer;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
        text-decoration: underline;
    }
`;

const ActionsCell = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
`;

const Data = styled.div`
    margin-bottom: 100px;

    @media (min-width: 935px) {
        width: 100%;

        .MuiDataGrid-root {
            width: 100%;
        }

        .MuiDataGrid-virtualScroller {
            overflow-x: hidden !important;
        }
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 48px;
    padding-bottom: 24px;
`;

const SubTitle = styled.p`
    color: #181122;
    text-align: center;
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
`;

export default function Invoices() {
    const api = Api();
    const storage = Storage();
    const { impersonateCompanyId } = useImpersonation();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [planInfo, setPlanInfo] = useState({
        status: "Assinatura ativa",
        users: 0,
        maxUsers: 3,
        periodicity: "...",
        price: "...",
        nextBilling: "...",
    });
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString || typeof dateString !== "string") return "-";
        // Suporta formato YYYY-MM-DD ou ISO
        const parts = dateString.split("-");
        if (parts.length === 3) {
            const [year, month, day] = parts;
            return `${day}/${month}/${year}`;
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("pt-BR");
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return "-";
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (isNaN(numValue)) return "-";
        return `R$ ${numValue.toFixed(2).replace(".", ",")}`;
    };

    const mapStatus = (apiStatus) => {
        const statusMap = {
            "PENDING": "PENDENTE",
            "OVERDUE": "VENCIDA",
            "RECEIVED": "PAGO",
            "CONFIRMED": "PAGO",
            "RECEIVED_IN_CASH": "PAGO",
        };
        const normalizedStatus = apiStatus?.toUpperCase() || "";
        return statusMap[normalizedStatus] || "PENDENTE";
    };

    const RenderStatus = (props) => {
        const { value } = props;

        if (value === "PENDENTE") {
            return <Tag className="pendente">Pendente</Tag>;
        }
        if (value === "VENCIDA") {
            return <Tag className="vencida">Vencida</Tag>;
        }
        if (value === "PAGO") {
            return <Tag className="pago">Pago</Tag>;
        }
        return <Tag className="pendente">{value}</Tag>;
    };

    const RenderActions = (props) => {
        const { row } = props;

        return (
            <ActionsCell>
                {row.invoiceUrl && (
                    <Tooltip
                        title="Fatura"
                        placement="top"
                        arrow
                    >
                        <ActionLink
                            href={row.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 8.66667L8 10.6667L14 4.66667" stroke={row.status === "PAGO" ? "#57C4C2" : "#000"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 8V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H10.6667" stroke={row.status === "PAGO" ? "#57C4C2" : "#000"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </ActionLink>
                    </Tooltip>
                )}
                {row.status === "PAGO" && row.nfUrl && (
                    <Tooltip
                        title="NF"
                        placement="top"
                        arrow
                    >
                        <ActionLink
                            href={row.nfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9.33333 1.33333H4C3.64638 1.33333 3.30724 1.47381 3.05719 1.72386C2.80714 1.97391 2.66667 2.31304 2.66667 2.66667V13.3333C2.66667 13.687 2.80714 14.0261 3.05719 14.2761C3.30724 14.5262 3.64638 14.6667 4 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2761C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V5.33333L9.33333 1.33333Z" stroke="#57C4C2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M9.33333 1.33333V5.33333H13.3333" stroke="#57C4C2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </ActionLink>
                    </Tooltip>
                )}
                {(row.status === "PENDENTE" || row.status === "VENCIDA") && row.secondCopyUrl && (
                    <Tooltip
                        title="2ª via"
                        placement="top"
                        arrow
                    >
                        <ActionLink
                            href={row.secondCopyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.3333 6V2.66667C13.3333 2.31305 13.1929 1.97391 12.9428 1.72386C12.6928 1.47381 12.3536 1.33334 12 1.33334H4C3.64638 1.33334 3.30724 1.47381 3.05719 1.72386C2.80714 1.97391 2.66667 2.31305 2.66667 2.66667V13.3333C2.66667 13.687 2.80714 14.0261 3.05719 14.2761C3.30724 14.5262 3.64638 14.6667 4 14.6667H8" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12 11.3333V14.6667" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10.6667 13H13.3333" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </ActionLink>
                    </Tooltip>
                )}
            </ActionsCell>
        );
    };

    const columns = [
        {
            field: "createdAt",
            headerName: "Data",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => formatDate(params.row.createdAt),
        },
        {
            field: "dueDate",
            headerName: "Vencimento",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => formatDate(params.row.dueDate),
        },
        {
            field: "amount",
            headerName: "Valor Cobrado",
            flex: 1,
            minWidth: 160,
            renderCell: (params) => formatCurrency(params.row.amount),
        },
        {
            field: "paidAmount",
            headerName: "Valor Pago",
            flex: 1,
            minWidth: 130,
            renderCell: (params) => formatCurrency(params.row.paidAmount),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 110,
            renderCell: RenderStatus,
        },
        {
            field: "actions",
            headerName: "Ações",
            flex: 1.5,
            minWidth: 180,
            sortable: false,
            renderCell: RenderActions,
        },
    ];

    const getInvoices = async () => {
        setLoading(true);
        try {
            const data = await storage.getItem("data");
            if (!data) return;

            const profileResponse = await api.getProfile(
                data.token,
                impersonateCompanyId
            );
            setProfile(profileResponse?.data?.profile?.profile);

            let subscriptionDetails;
            try {
                subscriptionDetails = await api.getSubscriptionDetails(
                    data.token,
                    impersonateCompanyId
                );

                if (subscriptionDetails?.errors || subscriptionDetails?.data?.errors) {
                    console.error("Erro ao buscar detalhes da assinatura:", subscriptionDetails);
                    setShowErrorMessage(true);
                    return;
                }
            } catch (error) {
                console.error("Erro ao buscar detalhes da assinatura:", error);
                setShowErrorMessage(true);
                return;
            }

            const subscriptionInfo = subscriptionDetails?.data?.subscriptionDetails;

            if (subscriptionInfo) {
                setPlanInfo({
                    name: "Plano Gratuito",
                    status: subscriptionInfo.status === "ACTIVE" ? "Assinatura ativa" : "Sem assinatura",
                    users: subscriptionInfo.activeUsersCount || 0,
                    maxUsers: subscriptionInfo.planMaxUsers || 3,
                    periodicity: subscriptionInfo.planCycle === "MONTHLY" ? "Mensal" : "Anual",
                    price: subscriptionInfo.planPrice
                        ? `R$ ${subscriptionInfo.planPrice.toFixed(2).replace(".", ",")}`
                        : "Gratuito",
                    nextBilling: subscriptionInfo.nextDueDate
                        ? formatDate(subscriptionInfo.nextDueDate)
                        : "-",
                });
            }

            // Buscar histórico de pagamentos
            const payments = (subscriptionInfo?.paymentHistory || []).slice(0, 12);

            // Mapear para formato da UI
            const mappedInvoices = payments.map((payment, index) => ({
                id: payment.id || index + 1,
                createdAt: payment.createdAt?.split(" ")[0] || payment.dueDate,
                dueDate: payment.dueDate,
                status: mapStatus(payment.status),
                amount: payment.amount,
                paidAmount: payment.paidDate ? payment.amount : null,
                invoiceUrl: payment.invoiceUrl,
                secondCopyUrl: payment.bankSlipUrl,
                nfUrl: null, // Não disponível na API
            }));

            setRows(mappedInvoices);
        } catch (error) {
            console.error("Erro ao buscar faturas:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Calcula a porcentagem considerando o máximo do plano
    const usersPercentage = (() => {
        // Se ainda não carregou os dados, não mostra nada
        if (planInfo.users === 0 && planInfo.maxUsers === 3) {
            return 0;
        }
        if (planInfo.maxUsers <= 0) return 0;
        return Math.min(Math.max((planInfo.users / planInfo.maxUsers) * 100, 0), 100);
    })();

    if (showErrorMessage) {
        return <Holder>
            <Content title="Faturas">
                <Box style={{ marginBottom: 40 }}>
                <Container>
                        <img src='/img/maintenance.svg' alt='imagem de alerta' title='imagem de alerta' />
                    <SubTitle>
                       Não foi possível concluir a solicitação agora.<br />
                       Tente novamente ou recarregue a página.
                    </SubTitle>
                </Container>
            </Box>
            </Content>

        </Holder>;
    }

    return (
        <>
            {loading && <Loader />}
            <Holder>
                <Content title="Faturas">
                    <PlanSection>
                        <PlanHeader>
                            <PlanInfo>
                                <PlanSubtitle active={planInfo.status === "Assinatura ativa"}>{planInfo.status}</PlanSubtitle>
                            </PlanInfo>
                            {profile?.permissions?.plans && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{
                                        fontSize: 12,
                                        padding: "10px 24px",
                                        textTransform: "uppercase",
                                    }}
                                    onClick={() => {
                                        window.location.href = "/settings?openPlans=true";
                                    }}
                                >
                                    Alterar Plano
                                </Button>
                            )}
                        </PlanHeader>

                        <PlanDetails>
                            <UsersProgress>
                                <PlanDetailLabel>Usuários</PlanDetailLabel>
                                <PlanDetailValue>{planInfo.users}</PlanDetailValue>
                                {!['zag-special-yearly', 'zag-special-monthly'].includes(profile?.company?.recurringPlanId) && (
                                    <>
                                        <ProgressBar>
                                            <ProgressFill style={{ width: `${usersPercentage}%` }} />
                                        </ProgressBar>
                                        <ProgressText>
                                            {planInfo.users} de {planInfo.maxUsers}
                                        </ProgressText>
                                    </>
                                )}
                            </UsersProgress>

                            <PlanDetailItem>
                                <PlanDetailLabel>Periodicidade</PlanDetailLabel>
                                <PlanDetailValue>{planInfo.periodicity}</PlanDetailValue>
                            </PlanDetailItem>

                            <PlanDetailItem>
                                <PlanDetailLabel>Valor da assinatura</PlanDetailLabel>
                                <PlanDetailValue>{planInfo.price}</PlanDetailValue>
                            </PlanDetailItem>

                            <PlanDetailItem>
                                <PlanDetailLabel>Próxima cobrança</PlanDetailLabel>
                                <PlanDetailValue>{planInfo.nextBilling}</PlanDetailValue>
                            </PlanDetailItem>
                        </PlanDetails>
                    </PlanSection>

                    <BasicTitle title="Últimas Faturas" />

                    <Data>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            // initialState={{
                            //     pagination: {
                            //         paginationModel: { pageSize: 12 },
                            //     },
                            // }}
                            // pageSizeOptions={[12, 24, 50]}
                            hideFooterPagination
                            hideFooter
                            disableRowSelectionOnClick
                            disableCellSelectionOnClick
                            sx={{
                                "& .MuiDataGrid-cell": {
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiDataGrid-cell:focus": {
                                    outline: "none",
                                },
                                "& .MuiDataGrid-cell:focus-within": {
                                    outline: "none",
                                },
                                "& .MuiDataGrid-columnHeader:focus": {
                                    outline: "none",
                                },
                                "& .MuiDataGrid-columnHeader:focus-within": {
                                    outline: "none",
                                },
                                "@media (min-width: 935px)": {
                                    "& .MuiDataGrid-virtualScroller": {
                                        overflowX: "hidden",
                                    },
                                    "& .MuiDataGrid-main": {
                                        overflowX: "hidden",
                                    },
                                },
                            }}
                        />
                    </Data>
                </Content>
            </Holder>
        </>
    );
}
